import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import UploadBanner from '../UploadBanner';
import SourceUploadPanel from './SourceUploadPanel';
import AddFootagePanel from './AddFootagePanel';
import PhotoDropzone from './PhotoDropzone';
import PhotoUploadDialog from './PhotoUploadDialog';
import MediaUploadProgress from './MediaUploadProgress';
import PhotoDayGrid from './PhotoDayGrid';
import PhotoCompileCta, { VARIANT_ACTION, VARIANT_FULL } from './PhotoCompileCta';
import CompiledVideoCard from './CompiledVideoCard';
import EditorCta from './EditorCta';
import SourcePreview from './SourcePreview';
import VideoDropzone from './VideoDropzone';
import VideoUploadDialog from './VideoUploadDialog';
import FootageVideoList from './FootageVideoList';
import CombineFootageCta from './CombineFootageCta';
import FootageRotationControl from './FootageRotationControl';
import UpgradePromptDialog from '../UpgradePromptDialog';
import { UPSELL_PROMPTS } from '../../upsell/triggers';
import usePhotoUpload from '../../hooks/usePhotoUpload';
import useMultiVideoUpload from '../../hooks/useMultiVideoUpload';
import useProjectVideos from '../../hooks/useProjectVideos';
import { nextOrderBase } from '../../videoUpload/ordering';
import { persistFootageOrder } from '../../videoUpload/footageOrder';
import { persistFootageRotation } from '../../videoUpload/footageRotation';
import { normalizeRotation, swapsAxes } from '../../videoUpload/rotation';
import { combinePreconditions, groupIssuesByKey, isConcatInFlight } from '../../utils/footageConcat';
import { FOOTAGE } from '../../lib/operationCopy';
import usePosterUrls from '../../hooks/usePosterUrls';
import { useProjectCoverage } from '../../hooks/useProjectPhotos';
import useFeatureFlag from '../../hooks/useFeatureFlag';
import useAuthUser from '../../hooks/useAuthUser';
import useEntitlements from '../../hooks/useEntitlements';
import useAccountStorage from '../../hooks/useAccountStorage';
import useDaySelection from '../../hooks/useDaySelection';
import { readBatch } from '../../photoUpload/batchRecord';
import { formatBytes, poolBytesFromGb } from '../../photoUpload/caps';
import {
  ORIGIN_PHOTO_COMPILE, ORIGIN_VIDEO_CONCAT,
  canCombineFootage, canRecompile, photosSinceCompile, resolveVideoOrigin,
} from '../../utils/videoOrigin';
import { compiledVideoFilename } from '../../utils/videoFilename';
import { resolveSourceVideoMeta } from '../../utils/sourceVideoMeta';
import './PhotoIngest.css';

const formatDuration = (seconds) => {
    if (!seconds || seconds <= 0) return '--';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    return `${m}:${s.toString().padStart(2, '0')}`;
};

// One metadata list, not two. This card used to stack an UploadBanner (File
// Name / FPS / Resolution / Duration) on top of its own side column (Duration /
// Resolution / FPS) — the same four facts, twice, in two different layouts.
//
// This is the card for footage the user BROUGHT: an upload, or a combine of
// their own clips. A photo-compiled source gets CompiledVideoCard instead,
// which knows what went into it and can hand it back.
const SourceVideoCard = ({
    currentProject, videoUrl, onSourcePlay, localVideoMeta, isPreviewPending, onSourceError, footageVideos = [],
    onRotate = null, rotationDisabled = false,
    // Takes the user to the Edits tab. Same contract as CompiledVideoCard's
    // prop of this name: null when the host has no tab switcher, in which case
    // the CTA is not drawn rather than drawn dead.
    onOpenEdits = null,
}) => {
    // Duration / Resolution / FPS come from the analysis worker, which stamps
    // them only when it FINISHES — and the finalize trigger clears them again
    // whenever a new object claims the slot. So this card has a hole to fill,
    // and two things can fill it: the locally-probed metadata the banner used
    // to supply (an upload, this session only), and — for a COMBINE — the
    // clips' own ingest-probed numbers, which cover the several minutes
    // between "combine complete" and "analysis complete" on a reload.
    const { length, width, height, fps } = resolveSourceVideoMeta({
        video: currentProject.video,
        localVideoMeta,
        videoConcat: currentProject.videoConcat,
        footageVideos,
    });
    // No `coverage` arg: that feeds the photo-compile branch only, and a
    // combine's range comes from videoConcat.combinedFrom.
    const fileName = resolveVideoOrigin(currentProject) === ORIGIN_VIDEO_CONCAT
        ? compiledVideoFilename(currentProject)
        : ((currentProject.video?.storagePath || '').split('/').pop() || '--');
    const rotation = normalizeRotation(currentProject.footageRotation);

    return (
        <div className="pv-source-video-card">
            <div className="pv-main-content">
                <div className="pv-left-column">
                    <SourcePreview
                        video={currentProject.video}
                        videoUrl={videoUrl}
                        rotation={currentProject.footageRotation}
                        width={width}
                        height={height}
                        isPreviewPending={isPreviewPending}
                        onPlay={onSourcePlay}
                        onError={onSourceError}
                        onRotate={onRotate}
                        rotateDisabled={rotationDisabled}
                    />
                </div>
                <div className="pv-right-column">
                    <div className="pv-source-meta" data-testid="source-video-meta">
                        <div className="pv-source-meta-row">
                            <span className="stat-label">File name</span>
                            <span className="stat-value pv-source-meta-filename">{fileName}</span>
                        </div>
                        <div className="pv-source-meta-row">
                            <span className="stat-label">Duration</span>
                            <span className="stat-value">{formatDuration(length)}</span>
                        </div>
                        <div className="pv-source-meta-row">
                            <span className="stat-label">Resolution</span>
                            {/* Reported the way the footage now reads, not the
                                way the file is stored. A quarter turn makes
                                the picture beside this row portrait, and a
                                landscape number next to a portrait preview is
                                the kind of disagreement that sends a user
                                looking for a bug that isn't there. */}
                            <span className="stat-value">
                                {(width && height)
                                    ? (swapsAxes(rotation) ? `${height}x${width}` : `${width}x${height}`)
                                    : '--'}
                            </span>
                        </div>
                        <div className="pv-source-meta-row">
                            <span className="stat-label">FPS</span>
                            <span className="stat-value">{fps ? Math.round(fps) : '--'}</span>
                        </div>
                    </div>
                    {/* The way forward. This column used to END at the metadata
                        list, so a project whose source was UPLOADED or COMBINED
                        got a player, four facts, and no route onward — the tab
                        named the Edits tab nowhere at all. The compiled card
                        beside this one has offered that route since it shipped,
                        which made the gap a difference in where the footage came
                        from rather than anything the user did. Same button, same
                        place in the reading order (after the facts, not over
                        them), so both cards end the same way. */}
                    {onOpenEdits && (
                        <div className="pv-source-actions">
                            <EditorCta
                                project={currentProject}
                                onClick={onOpenEdits}
                                testId="source-video-edit-cta"
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// FootageTab has three orthogonal concerns:
//   1) source video: the phase-1 flow, unchanged when a video exists
//   2) photos: preflight dialog → upload progress card → day grid + compile CTA
//   3) add-footage affordances: video picker (existing) + photo dropzone (new)
//
// Rendered order tries to keep whatever is "in flight" at the top, so the
// user's eye lands on the thing that's still moving.
const FootageTab = ({
    currentProject,
    isAnalysisPending,
    localVideoMeta,
    uploading,
    uploadProgress,
    onFileChange,
    videoUrl,
    isPreviewPending,
    onSourceError,
    onSourcePlay,
    projectId,
    // Compiling now costs credits: this routes a shortfall into ProjectView's
    // InsufficientCreditsDialog, the same one the render path opens.
    onCompileInsufficientCredits,
    // ProjectView's tab switcher. Optional: the legacy layout and the tests
    // that render this tab standalone pass nothing, and BOTH source-video
    // cards simply drop their Editor CTA when there is nowhere to send the
    // user.
    onTabChange = null,
}) => {
    // The source video is the INPUT to an edit, so the card that shows it is
    // the natural place to offer the next step — and that is true however the
    // video got here, so both cards take this: CompiledVideoCard for a
    // photo-compile, SourceVideoCard for an upload or a combine. Only a card
    // that HAS a video gets it: the empty-state band returns before
    // CompiledVideoCard reads the prop, so passing it there would name a route
    // that cannot render.
    const openEdits = onTabChange ? () => onTabChange('edits') : null;
    const hasVideo = !!currentProject.video || isAnalysisPending;
    const { enabled: photoIngestEnabled } = useFeatureFlag('photoIngestV1', { defaultValue: true });
    // Multi-video footage upload (Phase 1). Default OFF: with the flag unset
    // this tab renders exactly what it rendered before — the single-video
    // picker, the photo dropzone, and nothing else.
    const { enabled: multiVideoEnabled } = useFeatureFlag('multiVideoV1', { defaultValue: false });
    const { coverage } = useProjectCoverage(photoIngestEnabled ? projectId : null);
    const totalPhotos = coverage?.totalPhotos || 0;
    const totalPhotoBytes = coverage?.totalSizeBytes || 0;

    // Pooled per-account storage entitlement (#362, #342): the upload preflight
    // meters a batch against the tier's pooled allowance across all projects —
    // the only ceiling there is. `account.usedBytes` is the server-maintained
    // aggregate, so it already includes source videos and renders (#412), not
    // just this project's photos.
    const user = useAuthUser();
    const entitlements = useEntitlements(user);
    const { storageGb, tier, retentionClass, subscription, loading: entitlementsLoading } = entitlements;
    const account = useAccountStorage(photoIngestEnabled ? user : null);

    // #346: the compile callable refused because the account is over its
    // pooled allowance. Holds the server's usedBytes/poolBytes so the prompt
    // meters the numbers the SERVER refused on, not the client's cached
    // rollup, which can be a batch behind after an upload.
    const [storageFrozen, setStorageFrozen] = useState(null);

    const upload = usePhotoUpload(projectId, {
        existingPhotos: totalPhotos,
        existingBytes: totalPhotoBytes,
        poolTotalBytes: poolBytesFromGb(storageGb),
        poolUsedBytes: account.usedBytes,
        // Fail-closed while EITHER pooled input is still resolving. The rollup
        // load window leaves poolUsedBytes at a stale 0; the entitlements load
        // window leaves storageGb at the Free 2 GB default, which would wrongly
        // meter (and block) a Solo/Pro user against a 2 GB ceiling. Withhold the
        // pooled verdict until both the ceiling and the usage are known (#362).
        poolLoading: account.loading || entitlementsLoading,
    });

    // Footage videos (multiVideoV1). The subscription is skipped entirely when
    // the flag is off so a flag-off project issues no extra Firestore reads.
    const { videos: footageVideos } = useProjectVideos(multiVideoEnabled ? projectId : null);
    const footageCount = footageVideos.length;
    const footageBytes = footageVideos.reduce((a, v) => a + (v.sizeBytes || 0), 0);
    // One past the highest stored `order`, NOT the count — a partially-failed
    // batch leaves gaps, and a count would then hand a new clip an `order` some
    // existing clip already holds.
    const footageOrderBase = nextOrderBase(footageVideos);
    // The probe writes a poster frame per clip; this resolves the gs:// paths
    // into <img>-able URLs. Empty (and free) until the trigger has run.
    const posterUrls = usePosterUrls(footageVideos);

    const videoUpload = useMultiVideoUpload(projectId, {
        existingVideos: footageCount,
        existingBytes: footageBytes,
        orderBase: footageOrderBase,
        // Photos XOR videos (v1 product decision) — evaluated in both
        // directions from the same helper, so the two dropzones can't disagree.
        hasPhotos: totalPhotos > 0,
        hasLegacySourceVideo: hasVideo,
        poolTotalBytes: poolBytesFromGb(storageGb),
        poolUsedBytes: account.usedBytes,
        poolLoading: account.loading || entitlementsLoading,
    });

    // --- Combine footage (multiVideoV1, Phase 3) --------------------------
    // A combine the server is running right now. Both the reorder and the
    // delete are refused while it holds: the worker is streaming these exact
    // objects in this exact order, and `delete_footage_clip` returns
    // CONCAT_IN_FLIGHT for the same reason. Shared with Overview and the
    // editor, which show the same footage and offer the same rotate control.
    const concatInFlight = isConcatInFlight(currentProject.videoConcat);
    const footageUploadInFlight = videoUpload.phase === 'uploading';
    const footageLocked = concatInFlight || footageUploadInFlight;

    // WHETHER the pre-combine rotate control is live. Exactly one control ever
    // writes footageRotation — two of them would disagree while a write was in
    // flight — so this is the whole choice: before a combine the clip list's
    // chips row carries it (at every angle; it grows in place rather than
    // moving), and once a combine has claimed the source slot the player bar's
    // own control takes over and this one goes.
    const showChipsRotateControl = !hasVideo;

    // Per-clip refusals the combine callable returned (details.issues). Lives
    // here, not in the CTA, because the LIST renders them and the list is above
    // the CTA in the DOM — so they travel up from the CTA and back down here.
    const [clipIssues, setClipIssues] = useState([]);

    // The local mirror of the server's compatibility rule, surfaced onto the
    // rows before the user clicks anything.
    //
    // Two of the three kinds of precondition failure are marked, and the split
    // is the point:
    //   - INCOMPATIBLE: a permanent property mismatch. Marked.
    //   - not ready, TERMINAL (`status: 'error'`): the probe failed, the clip
    //     will never become usable, and the CTA is telling the user to "remove
    //     it" — so the list has to say which one "it" is. Marked, in the same
    //     red border + inline badge as the incompatible case.
    //   - not ready, TRANSIENT (`uploading` / `probing`): still on its way to
    //     ready. NOT marked: the row's own status cell already says "Reading
    //     file…", and a red warning on every clip of a healthy upload is an
    //     alarm about nothing the user can act on. It clears itself.
    const { incompatible, notReadyTerminal } = useMemo(
        () => combinePreconditions(footageVideos),
        [footageVideos],
    );
    const localClipIssues = useMemo(
        () => [...notReadyTerminal, ...incompatible],
        [notReadyTerminal, incompatible],
    );
    const footageIssuesByKey = useMemo(
        () => groupIssuesByKey([...localClipIssues, ...clipIssues]),
        [localClipIssues, clipIssues],
    );

    // Optimistic order, so a ↑/↓ click moves the row now instead of after the
    // Firestore round trip. Cleared as soon as the subscription reports the
    // written order, which is the authority — see below.
    const [pendingOrder, setPendingOrder] = useState(null);
    const displayedFootage = pendingOrder || footageVideos;

    const handleMoveClip = useCallback(async (fromIndex, toIndex) => {
        const current = pendingOrder || footageVideos;
        try {
            const { videos: next } = await persistFootageOrder(
                projectId, current, fromIndex, toIndex,
            );
            setPendingOrder(next);
        } catch (err) {
            // Drop the optimistic list rather than leave the user looking at an
            // order that was never written — the subscription's copy is the one
            // the combine will actually use.
            console.error('FootageTab: could not persist the clip order', err);
            setPendingOrder(null);
        }
    }, [projectId, footageVideos, pendingOrder]);

    // No optimistic state, unlike the reorder above: the write is one integer
    // on the project document the parent already subscribes to, so the live
    // snapshot turns the preview a beat later on its own. A local copy here
    // would only be a second source of truth to keep in step.
    //
    // Errors are surfaced rather than swallowed — a rotation that silently
    // failed to save would be discovered at render, on a video the user has
    // already paid credits for.
    //
    // The MESSAGE is held here, not in the control, because the tab outlives
    // every surface that can show it. The pre-combine control lives inside the
    // clip LIST, which unmounts whenever the footage empties and refills, and
    // the post-combine path reports through the player bar instead — one owner
    // here means a failure is reported the same way on both.
    const [rotateError, setRotateError] = useState(null);
    const handleRotateFootage = useCallback(
        async (next) => {
            setRotateError(null);
            try {
                return await persistFootageRotation(projectId, next);
            } catch (err) {
                setRotateError(err?.message || 'Could not save the rotation.');
                // Rethrown, not swallowed: both callers await this — the
                // pre-combine control to drop its busy state, and the player
                // bar's own control (VideoControlBar) to show its own message.
                throw err;
            }
        },
        [projectId],
    );

    // Hand authority back to the server once its snapshot agrees with what we
    // wrote (or once the list changes under us — a clip deleted in another tab
    // must not be re-shown by a stale optimistic copy).
    const footageOrderKey = footageVideos.map((v) => v.id).join('|');
    useEffect(() => {
        setPendingOrder((pending) => {
            if (!pending) return null;
            const settled = pending.map((v) => v.id).join('|') === footageOrderKey;
            return settled ? null : pending;
        });
    }, [footageOrderKey]);

    const handleDeleteClip = useCallback(async (video) => {
        // Server-owned: the callable releases the storage object and the
        // account's metered bytes with it, neither of which the client can do.
        try {
            const deleteClip = httpsCallable(getFunctions(), 'delete_footage_clip');
            await deleteClip({ projectId, videoKey: video.id });
            setClipIssues((issues) => issues.filter((issue) => issue.videoKey !== video.id));
        } catch (err) {
            // Read the structured reason, never err.message: on a transport
            // failure the SDK sets .message to the bare error code.
            const reason = err?.details?.reason;
            if (reason === 'CONCAT_IN_FLIGHT') {
                throw new Error('These clips are being combined right now — you can remove them once it finishes.');
            }
            if (reason === 'CLIP_NOT_FOUND' || err?.code === 'functions/not-found') {
                throw new Error('That clip is already gone.');
            }
            if (reason === 'MULTI_VIDEO_DISABLED') {
                throw new Error('Removing clips isn’t available yet.');
            }
            throw new Error('Could not remove this clip. Please try again.');
        }
    }, [projectId]);

    // The other direction of the XOR rule (photos refused on a footage project)
    // is NOT checked here. It was, and it was dead code in every UI state: the
    // photo dropzone is unmounted whenever `hasFootageVideos`, which is exactly
    // when the check could have fired. The race it was reaching for — a drop
    // that beats the footage subscription's first snapshot — is a client gate
    // that fails open by construction, so it is now enforced server-side in
    // on_project_photo_upload (which evicts the arriving object).
    const handleFiles = useCallback((files) => {
        upload.preflight(files);
    }, [upload]);

    const handleVideoFiles = useCallback((files) => {
        videoUpload.preflight(files);
    }, [videoUpload]);

    // The account-storage rollup is a one-shot read, so after a batch lands the
    // server has bumped this project's coverage but our poolUsedBytes is frozen
    // at its pre-upload value. Refresh it when an upload finishes so a second,
    // back-to-back drop in the same session meters against fresh pooled usage
    // instead of slipping over the allowance (#362). Keyed on the phase and
    // guarded by a ref so we reload once per completion, not on every re-render.
    const { reload: reloadAccount } = account;
    const reloadedForPhaseRef = useRef(null);
    useEffect(() => {
        const done = upload.phase === 'done' || upload.phase === 'partial';
        if (done && reloadedForPhaseRef.current !== upload.phase) {
            reloadedForPhaseRef.current = upload.phase;
            reloadAccount();
        } else if (!done) {
            // Re-arm once the flow leaves a terminal phase (idle/scanning/etc.)
            // so the next completion triggers a fresh reload.
            reloadedForPhaseRef.current = null;
        }
    }, [upload.phase, reloadAccount]);

    // Day selection (#385). Owned here because two siblings share it: the CTA
    // costs and sends the scope, the day grid renders the checkboxes. Seeded
    // from the selection the last compile persisted, so a rebuild resumes the
    // user's scope instead of silently reverting to every day.
    //
    // Declared above the photoIngestEnabled bail below — it is a hook, so it
    // must run on every render regardless of which branch we take.
    const daySelection = useDaySelection(coverage, currentProject?.photoCompile?.selection);

    // If the flag is off entirely, we render exactly the phase-1 UI — a
    // photo-agnostic footage tab that still uses SourceUploadPanel when
    // there's no video, and the source video card once there is one.
    if (!photoIngestEnabled) {
        if (!hasVideo) {
            return (
                <SourceUploadPanel
                    uploading={uploading}
                    uploadProgress={uploadProgress}
                    onFileChange={onFileChange}
                />
            );
        }
        if (uploading) {
            return (
                <div>
                    <UploadBanner state="uploading" uploadProgress={uploadProgress} />
                </div>
            );
        }
        return (
            <div>
                <SourceVideoCard
                    currentProject={currentProject}
                    videoUrl={videoUrl}
                    onSourcePlay={onSourcePlay}
                    localVideoMeta={localVideoMeta}
                    footageVideos={footageVideos}
                    isPreviewPending={isPreviewPending}
                    onSourceError={onSourceError}
                    onRotate={handleRotateFootage}
                    rotationDisabled={footageLocked}
                    onOpenEdits={openEdits}
                />
            </div>
        );
    }

    // Interrupted batch banner. Purely a client-side signal derived from a
    // sessionStorage record left behind if a batch never called
    // completeBatch(). Cheap to compute — the record is a couple of bytes.
    const interruptedBatch = totalPhotos > 0 && upload.phase !== 'uploading' && !!readBatch(projectId);

    const hasPhotos = totalPhotos > 0;
    const hasFootageVideos = multiVideoEnabled && footageCount > 0;
    const isBareProject = !hasVideo && !hasPhotos && !hasFootageVideos
        && upload.phase === 'idle' && videoUpload.phase === 'idle';

    // Append-and-recompile (#381). `recompilable` mirrors the server's
    // provenance rule (only a photo-compiled video may be rebuilt);
    // `sinceCompile` is the diff between the live coverage rollup and the
    // snapshot the compile worker persisted, which is what lets the CTA say
    // WHY you'd rebuild. Both are cheap derivations off data already loaded.
    const recompilable = canRecompile(currentProject);
    // The classification BEHIND that boolean. `recompilable` is false for both
    // an uploaded video and one whose provenance was never stamped, and the CTA
    // has to word its refusal differently for each, so it needs the origin
    // itself rather than the yes/no.
    const videoOrigin = resolveVideoOrigin(currentProject);
    const sinceCompile = photosSinceCompile(currentProject, coverage);
    const compileInFlight = ['queued', 'processing'].includes(currentProject.photoCompile?.status);

    // --- Which card shows the source video, and where the compile CTA lives --
    //
    // A video this project COMPILED is a different object to the user than a
    // video they uploaded: we know the days it covers, what went into it, and
    // how to hand it back with a name that means something. So it gets
    // CompiledVideoCard (download + recompile), and everything else keeps the
    // plain SourceVideoCard.
    const compiledSource = hasVideo && videoOrigin === ORIGIN_PHOTO_COMPILE;

    // Compile states that are CARDS in their own right — an in-flight run's
    // progress bar, a failed run's retry — must not be squeezed into the
    // 200px actions column of a card about a video that already exists. Gated
    // the same way PhotoCompileCta gates them internally (`compileStateIsOurs`)
    // so the two can't disagree about whether a status belongs to us.
    const compileStateIsOurs = !hasVideo || recompilable;
    const compileBusy = compileStateIsOurs
        && ['queued', 'processing', 'failed'].includes(currentProject.photoCompile?.status);

    // The CTA rides INSIDE the card whenever the card is the thing the user is
    // looking at: over a compiled video (State B's Recompile) or in the empty
    // band (State C's Compile). Otherwise it renders standalone exactly as
    // before — progress, retry, and the "can't compile over uploaded footage"
    // notice all keep their own card.
    const ctaInCard = hasPhotos && !compileBusy && !uploading
        && (compiledSource || !hasVideo);

    // ONE instance, always — pricing, preconditions, day scope and the
    // callable itself must not exist twice. Only its frame changes.
    const compileCta = hasPhotos ? (
        <PhotoCompileCta
            projectId={projectId}
            photoCount={totalPhotos}
            hasSourceVideo={hasVideo}
            photoCompile={currentProject.photoCompile}
            uploadInProgress={upload.phase === 'uploading'}
            recompilable={recompilable}
            videoOrigin={videoOrigin}
            sinceCompile={sinceCompile}
            variant={ctaInCard ? VARIANT_ACTION : VARIANT_FULL}
            // The other two halves of the server's compile precondition: a
            // charged render still reading the source, or the previous
            // compile's mp4 still landing. The CTA disables itself with a
            // reason rather than firing a request the callable would refuse
            // (#381).
            editedVideoStatus={currentProject.editedVideo?.status}
            sourceVideoStatus={currentProject.video?.status}
            daySelection={daySelection}
            coverage={coverage}
            // Running total of compile credits already charged on this project.
            // The server prices the next compile as the delta against it, so
            // the CTA has to quote the same delta or the price would change on
            // click.
            paidCredits={currentProject.compileBilling?.paidCredits}
            onInsufficientCredits={onCompileInsufficientCredits}
            onStorageFrozen={setStorageFrozen}
        />
    ) : null;

    return (
        <div>
            {uploading && (
                <UploadBanner state="uploading" uploadProgress={uploadProgress} />
            )}

            {upload.phase === 'uploading' && (
                <MediaUploadProgress
                    progress={upload.progress}
                    itemStates={upload.itemStates}
                    onCancel={upload.cancelUpload}
                    media="photos"
                />
            )}

            {upload.phase === 'partial' && upload.progress && (
                <div className="pi-banner-alert" data-testid="photo-upload-partial">
                    <div>
                        <b>{(upload.progress.failedCount || 0).toLocaleString()} of {(upload.progress.totalItems || 0).toLocaleString()} photos didn't upload.</b>{' '}
                        {(upload.progress.doneCount || 0).toLocaleString()} landed — the rest usually fail on a dropped
                        connection. Retry to finish uploading them.
                    </div>
                    <div className="pi-banner-actions">
                        <button type="button" className="btn primary pi-btn-sm" onClick={upload.retryFailed}>
                            Retry {(upload.progress.failedCount || 0).toLocaleString()} failed
                        </button>
                        <button type="button" className="btn ghost pi-btn-sm" onClick={upload.reset}>
                            Dismiss
                        </button>
                    </div>
                </div>
            )}

            {upload.reconcile?.status === 'recovered' && (
                <div className="pi-banner-alert" data-testid="photo-reconcile-recovered">
                    <div>
                        <b>Recovered {upload.reconcile.recovered.toLocaleString()} photo{upload.reconcile.recovered === 1 ? '' : 's'}.</b>{' '}
                        They finished uploading but didn't process the first time — they're all here now.
                    </div>
                    <div className="pi-banner-actions">
                        <button type="button" className="btn ghost pi-btn-sm" onClick={upload.dismissReconcile}>
                            Dismiss
                        </button>
                    </div>
                </div>
            )}

            {upload.reconcile?.status === 'incomplete' && (
                <div className="pi-banner-alert" data-testid="photo-reconcile-incomplete">
                    <div>
                        <b>{upload.reconcile.missing.toLocaleString()} photo{upload.reconcile.missing === 1 ? '' : 's'} didn't finish processing.</b>{' '}
                        Upload the same folder again to finish — already-uploaded photos are skipped.
                    </div>
                    <div className="pi-banner-actions">
                        <button type="button" className="btn ghost pi-btn-sm" onClick={upload.dismissReconcile}>
                            Dismiss
                        </button>
                    </div>
                </div>
            )}

            {interruptedBatch && (
                <div className="pi-banner-alert">
                    <b>Upload interrupted.</b> {totalPhotos.toLocaleString()} photos made it before the tab closed.
                    Upload the same folder again — we'll skip what's already here.
                </div>
            )}

            {hasVideo && !uploading && (compiledSource ? (
                <CompiledVideoCard
                    currentProject={currentProject}
                    coverage={coverage}
                    projectId={projectId}
                    videoUrl={videoUrl}
                    localVideoMeta={localVideoMeta}
                    isPreviewPending={isPreviewPending}
                    onSourcePlay={onSourcePlay}
                    onSourceError={onSourceError}
                    onRotate={handleRotateFootage}
                    rotationDisabled={footageLocked}
                    sinceCompile={sinceCompile}
                    photoCount={totalPhotos}
                    compileCta={ctaInCard ? compileCta : null}
                    onOpenEdits={openEdits}
                />
            ) : (
                <SourceVideoCard
                    currentProject={currentProject}
                    videoUrl={videoUrl}
                    onSourcePlay={onSourcePlay}
                    localVideoMeta={localVideoMeta}
                    footageVideos={footageVideos}
                    isPreviewPending={isPreviewPending}
                    onSourceError={onSourceError}
                    onRotate={handleRotateFootage}
                    rotationDisabled={footageLocked}
                    onOpenEdits={openEdits}
                />
            ))}

            {/* State C — photos, but nothing compiled from them yet. The band
                is the compile's home; while a run is queued/processing/failed
                the CTA's own progress or retry card takes its place, which is
                the state the user needs to see. `!uploading` matches the
                compiled card above: during a source-video upload the CTA moves
                to the standalone slot below, and a band whose only action has
                left is just a heading over a note about a button that is
                somewhere else. */}
            {!hasVideo && hasPhotos && !compileBusy && !uploading && (
                <CompiledVideoCard
                    currentProject={currentProject}
                    coverage={coverage}
                    projectId={projectId}
                    photoCount={totalPhotos}
                    compileCta={compileCta}
                />
            )}

            {hasPhotos && (
                <>
                    <div className="pi-header-chips">
                        <span className="pi-header-chip">
                            {totalPhotos.toLocaleString()} photo{totalPhotos === 1 ? '' : 's'}
                        </span>
                        <span className="pi-header-chip">
                            {formatBytes(totalPhotoBytes)}
                        </span>
                    </div>
                    {!ctaInCard && compileCta}
                    <PhotoDayGrid
                        projectId={projectId}
                        coverage={coverage}
                        // Checkboxes go away while a compile is actually
                        // running — the scope is already committed, so ticking
                        // days would do nothing until the next run (#385).
                        selectable={daySelection.choosing && !compileInFlight}
                        selectedDays={daySelection.selectedDays}
                        onToggleDay={daySelection.toggleDay}
                    />
                </>
            )}

            {hasFootageVideos && (
                <>
                    <FootageVideoList
                        videos={displayedFootage}
                        posterUrls={posterUrls}
                        issues={footageIssuesByKey}
                        onMove={handleMoveClip}
                        onDelete={handleDeleteClip}
                        mutationsDisabled={footageLocked}
                        mutationsDisabledReason={concatInFlight
                            ? (displayedFootage.length === 1
                                ? 'This clip can’t be removed while your video is being prepared.'
                                : 'Clips can’t be reordered or removed while they’re being combined.')
                            : 'Clips can’t be reordered or removed while an upload is running.'}
                        rotation={currentProject.footageRotation}
                        // Before a combine there is no source card, so the clip
                        // list's chips row is the only place the control can
                        // live — and the posters right below it are the only
                        // picture of the footage the user has. It stays there at
                        // every angle: the readout and Reset appear inside it
                        // rather than in a separate block elsewhere on the tab,
                        // so the first turn cannot move the button out from
                        // under the cursor.
                        rotateControl={showChipsRotateControl ? (
                            <FootageRotationControl
                                rotation={currentProject.footageRotation}
                                onChange={handleRotateFootage}
                                disabled={footageLocked}
                                error={rotateError}
                            />
                        ) : null}
                    />
                    <CombineFootageCta
                        projectId={projectId}
                        videos={displayedFootage}
                        videoConcat={currentProject.videoConcat}
                        // Whether a combine may claim the source-video slot.
                        // The client mirror of combine_footage's own
                        // VIDEO_ALREADY_EXISTS precondition: the server refuses
                        // the destructive case, this decides whether we OFFER
                        // it, so the user gets a reason instead of a toast.
                        combinable={canCombineFootage(currentProject)}
                        videoOrigin={videoOrigin}
                        uploadInProgress={footageUploadInFlight}
                        onIssues={setClipIssues}
                    />
                </>
            )}

            {multiVideoEnabled && videoUpload.phase === 'uploading' && (
                <MediaUploadProgress
                    progress={videoUpload.progress}
                    itemStates={videoUpload.itemStates}
                    onCancel={videoUpload.cancelUpload}
                    // This card was written for photos and reused here as-is,
                    // so an mp4 batch used to read "Uploading photos · 0 of 2"
                    // and advise re-picking a photo folder (#588).
                    media="clips"
                />
            )}

            {multiVideoEnabled && videoUpload.phase === 'partial' && videoUpload.progress && (
                <div className="pi-banner-alert" data-testid="video-upload-partial">
                    <div>
                        <b>{(videoUpload.progress.failedCount || 0).toLocaleString()} of {(videoUpload.progress.totalItems || 0).toLocaleString()} videos didn't upload.</b>{' '}
                        {(videoUpload.progress.doneCount || 0).toLocaleString()} landed — the rest usually fail on a
                        dropped connection. Retry to finish uploading them.
                    </div>
                    <div className="pi-banner-actions">
                        <button type="button" className="btn primary pi-btn-sm" onClick={videoUpload.retryFailed}>
                            Retry {(videoUpload.progress.failedCount || 0).toLocaleString()} failed
                        </button>
                        <button type="button" className="btn ghost pi-btn-sm" onClick={videoUpload.reset}>
                            Dismiss
                        </button>
                    </div>
                </div>
            )}

            {/* The videos-onto-a-photo-project half of the XOR rule — see
                upload/footageKind.js for the verdict, useMultiVideoUpload for
                the phase. (The other direction is enforced server-side; the
                photo dropzone is simply unmounted on a footage project.) */}
            {multiVideoEnabled && videoUpload.phase === 'conflict' && (
                <div className="pi-banner-alert" data-testid="video-footage-conflict">
                    <div>{videoUpload.conflict?.message}</div>
                    <div className="pi-banner-actions">
                        <button type="button" className="btn ghost pi-btn-sm" onClick={videoUpload.reset}>
                            Dismiss
                        </button>
                    </div>
                </div>
            )}

            {/* 'error' is the phase a thrown preflight (a scan that blew up, a
                signed-out session) lands in. Without this the tab just snapped
                back to the dropzone and the user was left guessing whether
                anything uploaded — so say what happened and offer the way out. */}
            {multiVideoEnabled && videoUpload.phase === 'error' && (
                <div className="pi-banner-alert" data-testid="video-upload-error">
                    <div>
                        <b>Something went wrong preparing these videos.</b>{' '}
                        Nothing was uploaded — try again.
                    </div>
                    <div className="pi-banner-actions">
                        <button type="button" className="btn ghost pi-btn-sm" onClick={videoUpload.reset}>
                            Dismiss
                        </button>
                    </div>
                </div>
            )}

            {isBareProject && (
                <AddFootagePanel
                    onFileChange={onFileChange}
                    onPhotoFiles={handleFiles}
                    onVideoFiles={multiVideoEnabled ? handleVideoFiles : undefined}
                />
            )}

            {/* Both pickers hide while EITHER batch is uploading (#588). They
                used to hide only for their own: mid video upload the photo
                dropzone still said "Add photos", inviting a second batch of the
                wrong medium into a project that is photos XOR videos anyway. */}
            {multiVideoEnabled && !isBareProject && !hasPhotos
                && videoUpload.phase !== 'uploading' && upload.phase !== 'uploading' && (
                <VideoDropzone
                    onFiles={handleVideoFiles}
                    // "clips" — the word the list header and the combine CTA
                    // directly above this both use (lib/operationCopy).
                    title={hasFootageVideos ? `Add more ${FOOTAGE.plural}` : `Add ${FOOTAGE.plural}`}
                    disabled={videoUpload.phase === 'scanning'}
                />
            )}

            {!isBareProject && upload.phase !== 'uploading'
                && videoUpload.phase !== 'uploading' && !hasFootageVideos && (
                <PhotoDropzone
                    onFiles={handleFiles}
                    title={
                        interruptedBatch
                            ? 'Upload the same folder again to finish'
                            : hasPhotos
                                ? 'Add more photos'
                                : 'Add photos'
                    }
                    subtitle={
                        interruptedBatch
                            ? 'Already-uploaded photos are detected and skipped — nothing uploads twice.'
                            : undefined
                    }
                    disabled={upload.phase === 'scanning'}
                />
            )}

            {upload.phase === 'preflight' && (
                <PhotoUploadDialog
                    phase={upload.phase}
                    scan={upload.scan}
                    dedup={upload.dedup}
                    caps={upload.caps}
                    retentionClass={entitlementsLoading ? null : retentionClass}
                    tier={tier}
                    entitlements={entitlements}
                    subscription={subscription}
                    onCancel={upload.reset}
                    onConfirm={upload.startUpload}
                />
            )}

            {multiVideoEnabled && videoUpload.phase === 'preflight' && (
                <VideoUploadDialog
                    phase={videoUpload.phase}
                    scan={videoUpload.scan}
                    dedup={videoUpload.dedup}
                    ordered={videoUpload.ordered}
                    caps={videoUpload.caps}
                    poolCaps={videoUpload.poolCaps}
                    onMove={videoUpload.moveItem}
                    onCancel={videoUpload.reset}
                    onConfirm={videoUpload.startUpload}
                />
            )}

            {/* #346 — the compile was refused because the account is over its
                storage allowance. Same prompt the upload preflight opens when a
                batch won't fit: it is the same wall, with the same two ways
                out. The meter reads the SERVER's numbers off the refusal, not
                the client rollup, which can be a batch behind. */}
            {storageFrozen && (
                <UpgradePromptDialog
                    promptId={UPSELL_PROMPTS.STORAGE_POOL_FULL}
                    entitlements={entitlements}
                    subscription={subscription}
                    usedBytes={storageFrozen.usedBytes}
                    totalBytes={storageFrozen.poolBytes}
                    projectId={projectId}
                    onFreeUpSpace={() => setStorageFrozen(null)}
                    onClose={() => setStorageFrozen(null)}
                />
            )}
        </div>
    );
};

export default FootageTab;
